/**
 * Purchase Order Row Component
 */

import { memo, useState } from 'react'
import {
  TableRow,
  TableCell,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  cn,
} from '@ufc/uikit'
import { MoreHorizontal, Eye, QrCode } from '@ufc/uikit'
import { useTranslation } from '@ufc/core'
import { poStatusConfig } from './constants'
import type { POTableColumnKey } from './constants'
import type { PurchaseOrder } from '../../domain/types'
import { downloadPurchaseOrderQrPdf } from '../../infrastructure/utils/po-qr-pdf'

export interface PurchaseOrderRowProps {
  purchaseOrder: PurchaseOrder
  isSelected: boolean
  visibleColumns: POTableColumnKey[]
  onClick: (po: PurchaseOrder) => void
}

function formatDate(value: string | null | undefined, locale: string): string {
  if (!value) return '\u2014'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '\u2014'
  return date.toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function stopPropagation(e: React.MouseEvent | React.PointerEvent) {
  e.stopPropagation()
}

/** Distinct material group codes across a PO's lines (material group is per-line). */
function getMaterialGroupCodes(po: PurchaseOrder): string[] {
  const codes = new Set<string>()
  for (const line of po.purchaseOrderLines ?? []) {
    if (line.sapMaterialGroup) codes.add(line.sapMaterialGroup)
  }
  return Array.from(codes)
}

function PurchaseOrderRowComponent({
  purchaseOrder: po,
  isSelected,
  visibleColumns,
  onClick,
}: PurchaseOrderRowProps) {
  const { t, i18n } = useTranslation()
  const visibleSet = new Set(visibleColumns)
  const status = po.status === 'closed' ? 'completed' : 'pending'
  const config = poStatusConfig[status]
  const StatusIcon = config.icon
  const [generatingQr, setGeneratingQr] = useState(false)

  const handleGenerateQr = async (e: React.MouseEvent) => {
    stopPropagation(e)
    if (generatingQr) return
    setGeneratingQr(true)
    try {
      await downloadPurchaseOrderQrPdf(Number(po.id))
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[PO QR] generate failed', err)
    } finally {
      setGeneratingQr(false)
    }
  }

  return (
    <TableRow
      className={cn(
        'border-border/30 cursor-pointer group transition-colors duration-150',
        isSelected
          ? 'bg-primary/8 hover:bg-primary/12 ring-1 ring-inset ring-primary/20'
          : 'hover:bg-muted/50'
      )}
      onClick={() => onClick(po)}
    >
      {visibleSet.has('poNumber') && (
        <TableCell className="px-5 py-4">
          <span className="font-semibold text-foreground text-sm">
            {po.poNumber}
          </span>
        </TableCell>
      )}
      {visibleSet.has('supplier') && (
        <TableCell className="px-5 py-4">
          <span className="text-sm">
            {po.supplier?.name || t('incomingMaterials.noSupplier')}
          </span>
        </TableCell>
      )}
      {visibleSet.has('factory') && (
        <TableCell className="px-5 py-4">
          <span className="text-sm">
            {po.factory?.name || '\u2014'}
          </span>
          {po.factory?.sapPlantCode && (
            <span className="text-xs text-muted-foreground block">
              {po.factory.sapPlantCode}
            </span>
          )}
        </TableCell>
      )}
      {visibleSet.has('materialGroup') && (
        <TableCell className="px-5 py-4">
          {(() => {
            const codes = getMaterialGroupCodes(po)
            if (codes.length === 0) {
              return <span className="text-muted-foreground text-sm">{'—'}</span>
            }
            return (
              <div className="flex flex-wrap items-center gap-1" dir="ltr">
                <Badge variant="secondary" className="font-medium">
                  {codes[0]}
                </Badge>
                {codes.length > 1 && (
                  <Badge variant="outline" className="font-medium" title={codes.join(', ')}>
                    {`+${codes.length - 1}`}
                  </Badge>
                )}
              </div>
            )
          })()}
        </TableCell>
      )}
      {visibleSet.has('poDate') && (
        <TableCell className="px-5 py-4">
          <span className="text-muted-foreground text-sm">
            {formatDate(po.poDate, i18n.language)}
          </span>
        </TableCell>
      )}
      {visibleSet.has('status') && (
        <TableCell className="px-5 py-4">
          <Badge className={cn(config.bg, config.text, 'border-0 font-semibold px-3 py-1 shadow-sm flex items-center gap-1.5 w-fit')}>
            <StatusIcon className="w-3.5 h-3.5" />
            <span className={cn('w-2 h-2 rounded-full animate-pulse', config.dotColor)} />
            {t(config.labelKey)}
          </Badge>
        </TableCell>
      )}
      {visibleSet.has('actions') && (
        <TableCell className="px-5 py-4 text-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={stopPropagation}>
              <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl hover:bg-muted">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { stopPropagation(e); onClick(po) }}>
                <Eye className="w-4 h-4 me-2" />
                {t('common.view')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleGenerateQr} disabled={generatingQr}>
                <QrCode className="w-4 h-4 me-2" />
                {generatingQr ? t('common.generating', { defaultValue: 'Generating…' }) : t('incomingMaterials.generateQrPdf', { defaultValue: 'Generate QR (PDF)' })}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      )}
    </TableRow>
  )
}

export const PurchaseOrderRow = memo(PurchaseOrderRowComponent, (prev, next) => {
  return (
    prev.purchaseOrder.id === next.purchaseOrder.id &&
    prev.isSelected === next.isSelected &&
    prev.purchaseOrder.status === next.purchaseOrder.status &&
    prev.visibleColumns === next.visibleColumns
  )
})
